import React, { useCallback, useEffect, useState } from "react";
import ProductListTable from "./ProductListTable/ProductListTable";
import ProductsFilters from "./ProductsFilters/ProductsFilters";
import { useGetBrandsQuery, useGetProductCategoriesQuery } from "@me-team/host/main/products/graphql/products.hooks";
import { Pagination } from "@services/ui-components";
import transformProductCategories from "@me-team/host/src/utils/transformProductCategories";
import {
    CombinedProductsInterface,
    ProductListInterfaceForWriteOffPage,
    WriteOffFilterInterface,
} from "../../../../interfaces/interfaces";
import { useParams } from "react-router-dom";
import WriteOffService from "../../../../services/WriteOffService/WriteOffService";
import { useGetProductListWriteOffPageQuery } from "@me-team/host/main/writeOffProducts/graphql/writeOffProducts.hooks";
import { useTranslation } from "react-i18next";
import { PreloaderComponent } from "@services/ui-components/src/PreloaderComponent/PreloaderComponent";

type ProductListProps = {
    combinedProducts: CombinedProductsInterface;
    addProductToWriteOff: (product: ProductListInterfaceForWriteOffPage) => void;
};

const ProductList: React.FC<ProductListProps> = (props) => {
    const { companyId, branchId } = useParams();
    const { t: translate } = useTranslation();
    const [filters, setFilters] = useState<WriteOffFilterInterface>({
        categoryName: null,
        categoryId: null,
        subCategoryId: null,
        brand: null,
    });
    const [productsData, setProductsData] = useState<ProductListInterfaceForWriteOffPage[]>(null);
    const numericCompanyId = Number(companyId);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [totalItems, setTotalItems] = useState(1);
    const [pageCount, setPageCount] = useState(0);

    const onApplyFilters = (filters: WriteOffFilterInterface) => {
        setFilters(filters);
    };

    const { data: productCategoriesData, loading: productCategoryLoading } = useGetProductCategoriesQuery({
        variables: {
            itemsPerPage: 1000,
            branchId: Number(branchId),
        },
        fetchPolicy: "network-only",
        context: {
            errorType: "local",
        },
    });

    const { data: brandsData, loading: brandsDataLoading } = useGetBrandsQuery({
        variables: {
            companyId: numericCompanyId,
            branchId: Number(branchId),
        },
        fetchPolicy: "network-only",
        context: {
            errorType: "local",
        },
    });
    const resolvedCategoryId = filters.categoryId
        ? Number(filters.categoryId)
        : filters.subCategoryId
          ? Number(filters.subCategoryId)
          : null;

    const { data: productListData, loading: productListLoading } = useGetProductListWriteOffPageQuery({
        variables: {
            companyId: numericCompanyId,
            itemsPerPage: itemsPerPage,
            page: currentPage,
            search: filters.categoryName,
            categoryId: resolvedCategoryId,
            brand: filters.brand,
            branchId: Number(branchId),
        },
        fetchPolicy: "network-only",
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            setPageCount(parseInt(data.user.company.productsList.pageCount || "0"));
            setTotalItems(data.user.company.productsList.totalCount || 0);
        },
    });

    useEffect(() => {
        if (productListData) {
            setProductsData(
                WriteOffService.productListInterfaceInit(
                    productListData.user.company.productsList.items,
                    props.combinedProducts
                )
            );
        }
    }, [productListData, props.combinedProducts]);

    const handlePageChange = useCallback((pageNumber: number) => {
        setCurrentPage(pageNumber);
    }, []);

    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <>
            {productsData && brandsData && productCategoriesData && (
                <>
                    <ProductsFilters
                        onApplyFilters={onApplyFilters}
                        productCategoriesList={transformProductCategories(
                            productCategoriesData?.productCategories?.items,
                            filters.categoryId,
                            filters.subCategoryId
                        )}
                        brandsList={WriteOffService.reactSelectBrands(brandsData.companyBrands)}
                    />
                    <ProductListTable productList={productsData} addProductToWriteOff={props.addProductToWriteOff} />
                    {totalItems > itemsPerPage && (
                        <div className="d-flex justify-content-between align-items-center my-4">
                            <Pagination
                                t={translate}
                                pages={Number(pageCount)}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                firstItemIndex={firstItemIndex}
                                lastItemIndex={lastItemIndex}
                                totalItems={totalItems}
                            />
                        </div>
                    )}
                </>
            )}
            {(productCategoryLoading || brandsDataLoading || productListLoading) && <PreloaderComponent />}
        </>
    );
};

export default ProductList;
